import { nodeRingIndex } from "../utils/nodes.js";
import { VIGOR_ZONE_NODE_COUNTS, vigorZoneLevel, } from "../config/vigor_zones.js";
function rotate(p, deg) {
    const r = (deg * Math.PI) / 180;
    return {
        x: p.x * Math.cos(r) - p.y * Math.sin(r),
        y: p.x * Math.sin(r) + p.y * Math.cos(r),
    };
}
/**
 * Build a slot from a set of global node ids: re-express them in a frame whose
 * origin is their centroid and whose +y points radially outward from the board
 * centre, then order canonically (outermost row first; left → right within a
 * row). The board centre is the layout origin, so node pixels are already
 * centre-relative.
 */
function makeSlot(id, zone, graph, ids) {
    const pts = ids.map((i) => graph.nodes[i].pixel);
    const cx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
    const cy = pts.reduce((s, p) => s + p.y, 0) / pts.length;
    const phi = (Math.atan2(cy, cx) * 180) / Math.PI; // radial direction
    const local = pts.map((p) => rotate({ x: p.x - cx, y: p.y - cy }, 90 - phi));
    const order = ids
        .map((_, i) => i)
        .sort((a, b) => {
        const dy = local[b].y - local[a].y;
        if (Math.abs(dy) > 0.05)
            return dy > 0 ? 1 : -1; // outermost (max y) first
        return local[a].x - local[b].x; // then left → right
    });
    return {
        id,
        zone,
        nodeIds: order.map((i) => ids[i]),
        localPos: order.map((i) => local[i]),
    };
}
/**
 * The 3 summit (diamond) slots, derived purely from geometry. The summit is the
 * innermost band of the board: the `VIGOR_ZONE_NODE_COUNTS` summit-count nodes
 * closest to the centre by ring distance. Those split into three 120° angular
 * sectors (centred at 30°, 150°, 270°) of 8 nodes each — a clean partition with
 * no shared nodes. (Zones themselves are stamped from the tiles placed here, not
 * pre-assigned; slots are fixed board positions, so they stay geometric.)
 */
export function deriveSummitSlots(graph) {
    const summitCount = VIGOR_ZONE_NODE_COUNTS[vigorZoneLevel("summit")];
    const summit = new Set([...graph.nodes]
        .sort((a, b) => nodeRingIndex(a) - nodeRingIndex(b) || a.id - b.id)
        .slice(0, summitCount)
        .map((n) => n.id));
    const slotOf = (p) => {
        const a = (Math.atan2(p.y, p.x) * 180) / Math.PI;
        return ((Math.round((a - 30) / 120) % 3) + 3) % 3;
    };
    const groups = [[], [], []];
    for (const n of graph.nodes) {
        if (summit.has(n.id))
            groups[slotOf(n.pixel)].push(n.id);
    }
    return groups.map((ids, k) => makeSlot(`summit-${k}`, "summit", graph, ids));
}
// --- Faithful board tessellation -----------------------------------------
//
// The board is a triangular-lattice hexagon of side 16: hex CENTRES form a
// triangular lattice, and every movement node sits at the centroid of its three
// hexes — i.e. at the centre of one small lattice triangle. So the 1536 nodes
// ARE the 6·16² small triangles, and a tile is a group of them whose edges run
// along hex-centre lines (between hexes, never on the corner nodes).
//
// Each of the 6 sectors (a side-16 equilateral triangle, apex at the board
// centre) splits into rows from the apex; row `r` holds `2r-1` triangles per
// sector. The zones are concentric row bands, and within a band the physical
// tile shapes fall out exactly:
//
//   rows 1–2   summit     24 = 3 diamonds  (each diamond spans 2 sectors)
//   rows 3–5   crag      126 = 6 trapezoids (1 per sector, 21 each)
//   rows 6–10  ridgeline 450 = 18 triangles (3 per sector, 25 each)
//   rows 11–15 foothill  750 = 30 triangles (5 per sector, 25 each)
//   row  16    forest    186 = 6 perimeter strips (1 per sector, 31 each)
//
// A node's row = ⌊triScale/3⌋+1 where triScale = max(|Σq|,|Σr|,|Σs|) over its
// three hexes (3× the centroid's ring distance, exact as an integer). Sector and
// in-row position come from the centroid angle. All of this is layout-scale
// independent, so client and server derive identical slot ids.
/** Lattice row (1-based, from the board centre) of a node's small triangle. */
function nodeRow(node) {
    let q = 0;
    let r = 0;
    let s = 0;
    for (const h of node.hexes) {
        q += h.q;
        r += h.r;
        s += h.s;
    }
    const triScale = Math.max(Math.abs(q), Math.abs(r), Math.abs(s));
    return Math.floor(triScale / 3) + 1;
}
/** Angle of a point in [0,360). */
function angleOf(p) {
    const a = (Math.atan2(p.y, p.x) * 180) / Math.PI;
    return ((a % 360) + 360) % 360;
}
/**
 * Sector 0..5 of a node. The 30° offset aligns the six bins with the concentric
 * hexagons' vertices, so each "row within a sector" is one straight hexagon edge
 * (a clean row parallel to the sector base) rather than bending across two —
 * verified to make every tile a convex diamond / trapezoid / triangle.
 */
const SECTOR_OFFSET_DEG = 30;
function sectorOf(p) {
    const a = (angleOf(p) + SECTOR_OFFSET_DEG) % 360;
    return Math.floor(a / 60) % 6;
}
/** Position of an angle within its sector bin, in [0,60). */
function angleInSector(angle, sector) {
    return ((angle + SECTOR_OFFSET_DEG) % 360) - sector * 60;
}
function zoneOfRow(row) {
    if (row <= 2)
        return "summit";
    if (row <= 5)
        return "crag";
    if (row <= 10)
        return "ridgeline";
    if (row <= 15)
        return "foothill";
    return "forest";
}
/**
 * Which side-5 sub-triangle (0-based) a node belongs to within its sector band,
 * for the triangle zones. `idx` is its left→right position in (sector, row),
 * `width` = 2·row-1 the row's triangle count in the sector.
 *
 * Ridgeline (rows 6–10) splits into 3 side-5 triangles (up, down, up); foothill
 * (rows 11–15) into 5 (up, down, up, down, up). Per local row `lr` (1..5) the
 * up-triangle corners are `2lr-1` wide and the down triangles `11-2lr` wide.
 */
function subTriangle(zone, row, idx, width) {
    if (zone === "ridgeline") {
        const lr = row - 5;
        const corner = 2 * lr - 1;
        if (idx < corner)
            return 0; // left up
        if (idx >= width - corner)
            return 2; // right up
        return 1; // middle down
    }
    // foothill: 5 segments [up, down, up, down, up]
    const lr = row - 10;
    const up = 2 * lr - 1;
    const down = 11 - 2 * lr;
    const segs = [up, down, up, down, up];
    let acc = 0;
    for (let k = 0; k < segs.length; k++) {
        acc += segs[k];
        if (idx < acc)
            return k;
    }
    return segs.length - 1;
}
/**
 * Tessellate the whole board into its physical tiles: 3 summit diamonds, 6 crag
 * trapezoids, 18 ridgeline + 30 foothill triangles, 6 forest perimeter strips
 * (63 total, covering all nodes). Deterministic and layout-independent, so the
 * client and server derive identical slot ids — only mutable node state syncs.
 */
export function deriveSlots(graph) {
    const infos = graph.nodes.map((n) => {
        const row = nodeRow(n);
        return {
            id: n.id,
            row,
            sector: sectorOf(n.pixel),
            zone: zoneOfRow(row),
            angle: angleOf(n.pixel),
        };
    });
    // For the triangle zones we need each node's left→right position within its
    // (sector, row), so sort those nodes by angle within the sector.
    const inRowIndex = new Map();
    const bySectorRow = new Map();
    for (const info of infos) {
        if (info.zone !== "ridgeline" && info.zone !== "foothill")
            continue;
        const key = `${info.sector}:${info.row}`;
        (bySectorRow.get(key) ?? bySectorRow.set(key, []).get(key)).push(info);
    }
    for (const group of bySectorRow.values()) {
        // Sort by angle within the sector so left→right is consistent across rows.
        group.sort((a, b) => angleInSector(a.angle, a.sector) - angleInSector(b.angle, b.sector));
        group.forEach((info, i) => inRowIndex.set(info.id, i));
    }
    const groups = new Map();
    const push = (key, zone, id) => {
        const g = groups.get(key) ?? groups.set(key, { zone, ids: [] }).get(key);
        g.ids.push(id);
    };
    for (const info of infos) {
        const { id, row, sector, zone } = info;
        switch (zone) {
            case "summit":
                // Two adjacent sectors' apex triangles fuse into one diamond.
                push(`summit-${Math.floor(sector / 2)}`, zone, id);
                break;
            case "crag":
            case "forest":
                push(`${zone}-${sector}`, zone, id);
                break;
            case "ridgeline":
            case "foothill": {
                const idx = inRowIndex.get(id) ?? 0;
                const sub = subTriangle(zone, row, idx, 2 * row - 1);
                push(`${zone}-${sector}-${sub}`, zone, id);
                break;
            }
        }
    }
    return [...groups.entries()].map(([id, g]) => makeSlot(id, g.zone, graph, g.ids));
}
//# sourceMappingURL=slots.js.map